import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography, Card, Grid } from "@mui/material";
import { Checkbox } from "@mui/material";
import { getLocalDate } from "../../../../utils/dateHelper";
import { getVisualField } from "../../../../utils/examReportsHelper";
import useClinic from "../../../../hooks/useClinic";

export default function SelectAnalysisExamCard({
  exam,
  isSelected,
  selectedExam,
  handleCardSelect,
  isDisabled,
}) {
  const { t } = useTranslation();
  const { clinicTimezone } = useClinic();

  const visualFieldSections = exam?.visualFieldThresholdingSections;
  const examTitle = visualFieldSections?.[0]?.algorithm.toLowerCase();
  const examGridType = getVisualField(visualFieldSections?.[0]?.gridType);

  const leftSideLabels = [
    { label: "FL", field: "fixationLossPercentage" },
    {
      label: "FN",
      fields: ["falseNegativeCount", "falseNegativeMisses"],
    },
    { label: "FP", field: "falsePositivePercentage" },
  ];

  const rightSideLabels = [
    { label: "PSD", field: "patternStandardDeviation" },
    { label: "MD", field: "totalMeanDeviation" },
    { label: "VRFI", field: "retinaLogikFieldIndex" },
  ];

  return (
    <Card
      data-testid={`select-analysis-card-${exam.id}`}
      sx={{
        minWidth: 340,
        backgroundColor: isSelected ? "#E2772E" : "#1E1E2F",
        color: "#FFF",
        borderRadius: 2,
        padding: 2,
        transition: "background-color 0.3s ease",
        display: "flex",
        flexDirection: "column",
        cursor: isDisabled ? "not-allowed" : "pointer",
      }}
      onClick={() => {
        if (!isDisabled) handleCardSelect(exam);
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Checkbox
            disabled={isDisabled}
            checked={isSelected}
            sx={{
              padding: 0,
              color: "#FFF",
              "&.Mui-checked": { color: "#fff" },
              "&.Mui-disabled": { color: "#fff" },
            }}
          />
          <Typography style={{ fontSize: "14px", fontWeight: "bold" }}>
            {t(examTitle)} | {examGridType}
          </Typography>
        </Box>
        <Box
          border="1px solid #fff"
          backgroundColor="#fff"
          p={0.5}
          borderRadius="3px"
        >
          <Typography
            fontWeight="bold"
            transition="color 0.3s ease"
            color={isSelected ? "#E2772E" : "#1E1E2F"}
            variant="body2"
          >
            {t("RetinaLogik")}
          </Typography>
        </Box>
      </Box>

      <Grid container gap={isSelected ? 2 : 0}>
        <Grid item xs={12}>
          <Box
            sx={{
              paddingTop: 1,
              paddingBottom: 0,
            }}
          >
            <Typography variant="body2">
              {t("progress_analysis_card_age")}: {exam.age}
            </Typography>
            <Typography variant="body2">
              {t("progress_analysis_exam_date")}:{" "}
              {getLocalDate(exam.completionDate, clinicTimezone)}
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Grid container>
            <Grid item xs={6}>
              {isSelected && selectedExam?.reportData?.stats && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {leftSideLabels.map(({ label, field, fields }, index) => {
                    let displayValue;

                    if (fields) {
                      const falseNegativeCount =
                        selectedExam.reportData?.stats[fields[0]];
                      const falseNegativeMisses =
                        selectedExam.reportData?.stats[fields[1]];

                      if (
                        falseNegativeCount !== null &&
                        !isNaN(falseNegativeCount) &&
                        falseNegativeMisses !== null &&
                        !isNaN(falseNegativeMisses)
                      ) {
                        displayValue = `${falseNegativeMisses || 0}/${falseNegativeCount || 0}`;
                      }
                    } else {
                      const value = selectedExam.reportData?.stats[field];

                      if (value !== null && !isNaN(value)) {
                        displayValue = Number(value).toFixed(2);
                      }
                    }

                    return displayValue ? (
                      <Typography key={index} variant="body2">
                        {label}: {displayValue}
                        {!fields && field !== "falseNegativeCount" && "%"}
                      </Typography>
                    ) : null;
                  })}
                </Box>
              )}
            </Grid>

            <Grid item xs={6}>
              {isSelected && selectedExam?.reportData.postProcessing && (
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  {visualFieldSections?.map((section, index) => (
                    <Box
                      key={section.id || index}
                      display="flex"
                      flexDirection="column"
                    >
                      {rightSideLabels.map(({ label, field }, index) => {
                        const value =
                          selectedExam.reportData.postProcessing[field];

                        if (value !== null && !isNaN(value)) {
                          return (
                            <Typography key={index} variant="body2">
                              {label}: {Number(value).toFixed(2)}
                              {field === "retinaLogikFieldIndex" && "%"}
                            </Typography>
                          );
                        }
                        return null; // Return null if the value is NaN or null
                      })}
                    </Box>
                  ))}
                </Box>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Card>
  );
}
